import { defaultSingleSelectionHeaderColumn } from '@/constants/ag-grid-scenario-builder.constant'
import { optimizationDefaultColDefs } from '@/constants/portfolioOptimization.constant'
import { IConstraint } from '@/interfaces/portfolioOptimization.interface'
import { removeConstraint } from '@/states/optimizationConstraint.slice'
import { portfolioOptimizationDefaultGridOptions } from '@/utils/grid-utils/colDefs'
import { ColDef, GridOptions, ValueGetterParams } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import DeleteIcon from '@mui/icons-material/Delete'
import { Box, IconButton } from '@mui/material'
import React, { useRef } from 'react'
import { useDispatch } from 'react-redux'

type OptimizationConstraintGridProps = {
  constraintList: IConstraint[]
}

const OptimizationConstraintGrid: React.FC<OptimizationConstraintGridProps> = ({
  constraintList,
}) => {
  const gridRef = useRef<AgGridReact>(null)
  const dispatch = useDispatch()
  const DeleteCellRenderer = ({ data }: { data: IConstraint }) => {
    const onDelete = () => {
      dispatch(removeConstraint(data.rowKey as string))
    }
    return (
      <IconButton onClick={onDelete} aria-label='delete' size='small'>
        <DeleteIcon />
      </IconButton>
    )
  }

  const gridOptions = {
    ...portfolioOptimizationDefaultGridOptions,
    sideBar: false,
    noRowsOverlayComponentParams: () => {
      return { noRowsMessage: 'No constraints available.' }
    },
    rowSelection: 'single' as const,
    columnDefs: [
      {
        ...defaultSingleSelectionHeaderColumn,
        cellRenderer: DeleteCellRenderer,
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Order',
        field: 'id',
        cellDataType: 'number',
        filter: 'agNumberColumnFilter',
        hide: true,
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Type',
        field: 'type',
        cellDataType: 'text',
        filter: 'agTextColumnFilter',
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Description',
        field: 'description',
        cellDataType: 'text',
        filter: 'agTextColumnFilter',
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Condition',
        field: 'operator',
        cellDataType: 'text',
        filter: 'agTextColumnFilter',
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Range',
        field: 'ranges',
        cellDataType: 'text',
        filter: 'agTextColumnFilter',
      },
      {
        ...optimizationDefaultColDefs,
        floatingFilter: false,
        headerName: 'Target',
        field: 'target',
        cellDataType: 'text',
        filter: 'agTextColumnFilter',
        cellStyle: { textAlign: 'left' },
        valueGetter: (params: ValueGetterParams) => {
          if (params.data?.target) {
            return params.data.target
          } else if (params.data?.targetString) {
            return params.data.targetString
          }
          return ''
        },
      },
    ] as ColDef[],
  } as GridOptions

  return (
    <>
      <Box className='ag-theme-quartz' boxSizing='border-box'>
        <AgGridReact
          ref={gridRef}
          rowData={constraintList}
          gridOptions={gridOptions}
          data-cy='optimization-constraint-ag-grid'
        ></AgGridReact>
      </Box>
    </>
  )
}

export default OptimizationConstraintGrid
